import type { MapDispatchToPropsFunction } from 'types/redux';
import * as tpa from '#packages/tpa';
import {
  EditorType,
  InstallationOriginType,
  InstallInitiator,
} from '@wix/platform-editor-sdk';
import * as util from '#packages/util';
import { wrapWithPropsTransformer } from '../utils';

import type { EditorState } from '#packages/stateManagement';
import type { EditorAPI } from '#packages/editorAPI';
import type { StateMapperArgs } from 'types/redux';
import type { Dispatch } from 'redux';
import * as coreBi from '#packages/coreBi';

export interface InstallAppWelcomeDispatchProps {
  installAppMainAction: (appDefinition: string) => void;
  openAppMarket: (appDefinition: string, appName: string) => void;
}

const getEditorAPI = (
  dispatch: Dispatch,
  getState: () => EditorState,
  { editorAPI }: StateMapperArgs,
): EditorAPI => editorAPI;

const mapDispatchToProps: MapDispatchToPropsFunction<
  InstallAppWelcomeDispatchProps,
  {}
> = (dispatch) => {
  const editorAPI = dispatch(getEditorAPI);
  return {
    installAppMainAction: async (appDefinitionId: string) => {
      if (!appDefinitionId) {
        throw new Error(`add panel app data missing appDefinitionId`);
      }
      const marketData =
        await editorAPI.dsRead.tpa.appMarket.getDataAsync(appDefinitionId);
      const shouldNavigate = marketData?.appFields?.shouldNavigate ?? true;
      const type = tpa.appMarketUtils.getAppType(marketData);
      const options = {
        shouldNavigate,
        headlessInstallation: Boolean(
          tpa.appMarketUtils.isHeadlessInstallation(marketData),
        ),
        showPageAddedPanel: !marketData?.doNotShowPageAddedPanel,
      };
      tpa.services.tpaAddAppService.addApp(
        editorAPI,
        appDefinitionId,
        null,
        null,
        type,
        tpa.constants.BI.type.ADD_APP_ADD_PANEL,
        false,
        { adding_method: 'click' },
        options,
        async () => {
          if (util.sections.isSectionsEnabled()) {
            await editorAPI.waitForChangesAppliedAsync();
            const selectedComponents =
              editorAPI.selection.getSelectedComponents();
            if (selectedComponents.length === 1) {
              editorAPI.components.hooks.componentAddedToStage.fire({
                compRef: selectedComponents[0],
                type: 'click',
                origin: 'add_panel',
              });
            }
          }
        },
        {
          type: EditorType.Classic,
          initiator: InstallInitiator.Editor,
          info: {
            type: InstallationOriginType.AddPanel,
          },
        },
      );
    },
    openAppMarket: (appDefinitionId: string, appName: string) => {
      const appMarketPanelProps = {
        urlParams: {
          slug: appDefinitionId,
        },
      };
      tpa.appMarketTabService.openAppMarketTab(
        appMarketPanelProps,
        editorAPI.panelManager.openPanel,
      );

      const biData = {
        app_id: appDefinitionId,
        app_name: appName,
      };
      editorAPI.bi.event(
        coreBi.events.addPanel.INSTALL_FOUND_OUT_MORE_CLICK,
        biData,
      );
    },
  };
};

export const installAppWelcomeMapper = wrapWithPropsTransformer<
  InstallAppWelcomeDispatchProps,
  {}
>(mapDispatchToProps, (props) => ({
  installAppMainAction: props.installAppMainAction,
  openAppMarket: props.openAppMarket,
}));
